import { Box } from '@redbubble/design-system';
import type { Tag } from 'pages/i/types';
import { useMemo, useRef } from 'react';
import { OptimisedSEOTag } from './OptimisedSEOTag';
import styles from './TagsList.module.scss';

interface TagsListProps {
  tags: Tag[];
  isTablet: boolean;
  indexableTags?: number;
}

export const TagsList = ({ tags, isTablet, indexableTags }: TagsListProps) => {
  const tagsListRef = useRef<HTMLDivElement>(null);

  const container = tagsListRef.current;

  const handleWheel = (event) => {
    if (container) {
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      let accumulatedScrollY = 0;
      const scrollYAmount = event.deltaX || event.deltaY || event.deltaZ; // handling mousewheel + touchpad

      accumulatedScrollY += scrollYAmount;

      const prevPosition = container.scrollLeft;
      const newPosition = prevPosition + accumulatedScrollY;

      let position = newPosition;
      // continue scrolling the page if you scroll past the start/end of the div
      const preventDefault = !(newPosition > maxScrollLeft || newPosition < 0);
      if (newPosition < 0) position = -prevPosition;
      if (newPosition > maxScrollLeft) position = maxScrollLeft;

      accumulatedScrollY = position;

      if (!isTablet && preventDefault) event.preventDefault();

      container.scrollLeft = accumulatedScrollY;
    }
  };

  const slides = useMemo(() => {
    return tags.map((tag, idx) => {
      return (
        <OptimisedSEOTag
          // There are no unique identifiers for tags but react requires unique keys so have assigned idx to key for unique identifier
          key={`optimisedTag-${tag.name}-${idx}`}
          tag={tag}
          tagItemKey={idx}
          seoHidden={indexableTags ? idx >= indexableTags : false}
        />
      );
    });
  }, [tags, indexableTags]);

  return (
    <Box paddingBottom="xs">
      <Box
        className={styles.tagsList}
        paddingTop="xs"
        display="flex"
        ref={tagsListRef}
        onMouseEnter={() => {
          tagsListRef.current?.addEventListener('wheel', handleWheel);
        }}
        onMouseLeave={() => {
          tagsListRef.current?.removeEventListener('wheel', handleWheel);
        }}
      >
        {slides.map((tag) => (
          <Box marginRight="m" marginBottom="xs" key={tag.key}>
            {tag}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
